import { Alert, AlertTitle, Box, Button, Divider, HStack, Icon, Image, Modal, ModalBody, ModalContent, ModalOverlay, Stack, Text, useDisclosure, useOutsideClick, type UseDisclosureReturn } from '@chakra-ui/react'
import { faPaperPlane, faRefresh } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Ellipsis, InputFormControl } from 'BrokerTsLib/src/components'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useRef } from 'react'
import root from 'react-shadow'
import * as yup from 'yup'

import { Apis } from '@/appSettings'
import { Loading } from '@/components'
import { DocumentsListItem } from '@/components/Documents/DocumentsList'
import { TextEditor } from '@/components/TextEditor'
import { useUrl } from '@/hooks'
import type { GetCalculationEmailResponse } from '@/hooks/useMailing'
import { useGetCalculationEmail, useSendCalculationEmail } from '@/hooks/useMailing'
import type { SendCalculationEmailRequest } from 'BrokerTsLib/src/dtos'
import { useCustomToast } from 'BrokerTsLib/src/hooks/useCustomToast'

interface SendCalculationContextProps {
  paramsId: string
  offerIds: number[]
  send: () => void
  isSending?: boolean
}

const SendCalculationContext = createContext<SendCalculationContextProps | null>(null)

export const SendCalculationProvider = ({ paramsId, offerIds, children, editable }: {
  paramsId: string
  offerIds: number[]
  children: ReactNode
  editable?: boolean
}) => {
  const disclosure = useDisclosure()
  const { onOpen, onClose } = disclosure

  useEffect(() => {
    if (!offerIds.length)
      onClose()
  }, [offerIds, onClose])

  const { mutateAsync, isPending: isSending } = useSendCalculationEmail()
  const { successToast, errorToast } = useCustomToast()

  const sendMutation = useCallback((v: Partial<SendCalculationFormValues> = {}) =>
    mutateAsync({ ...v, paramsId: Number(paramsId), insurerOfferIds: offerIds })
      .then(r => {
        onClose()
        successToast('E-mail byl úspěšně odeslán')(r)
      })
      .catch(errorToast('Nepodařilo se odeslat e-mail')),
  [mutateAsync, paramsId, offerIds, errorToast, onClose, successToast])

  const sendSwitched = useCallback(() => {
    if (editable)
      onOpen()
    else
      sendMutation() // sends default email without editing
  }, [onOpen, editable, sendMutation])

  return <SendCalculationContext.Provider value={{ paramsId, offerIds, send: sendSwitched, isSending }}>
    <SendCalculationModal {...disclosure} send={sendMutation} />
    {children}
  </SendCalculationContext.Provider>
}

export const useSendCalculationContext = () => {
  const context = useContext(SendCalculationContext)
  if (!context) {
    throw new Error('useSendCalculationContext must be used within a SendCalculationProvider')
  }
  return context
}

interface GetCalculationVehicleEmailResponse extends GetCalculationEmailResponse {
  offers: string
}

export const SendCalculationModal = ({ isOpen, onClose, send }: { send: (v: SendCalculationFormValues) => Promise<void> } & UseDisclosureReturn) => {
  const { paramsId, offerIds } = useSendCalculationContext()
  const { data, isLoading, isSuccess, isError } = useGetCalculationEmail<GetCalculationVehicleEmailResponse>(paramsId, offerIds, isOpen)

  const bodyRef = useRef<HTMLDivElement>(null)

  useOutsideClick({
    ref: bodyRef,
    handler: (e) => {
      const clickedElementIsInsideContent = bodyRef.current?.contains(e.target as Node)
      const isChildOfJodit = (child: Node | null): boolean => {
        let node = child
        while (node) {
          if (node instanceof HTMLElement && node.classList.contains('jodit')) {
            return true
          }
          node = node.parentNode
        }
        return false
      }

      if (!clickedElementIsInsideContent && !isChildOfJodit(e.target as Node)) {
        onClose()
      }
    }
  })

  const documentUrl = useUrl(Apis.document)
  const tiskUrl = `${documentUrl}/GetOffersPdf?paramsId=${paramsId ?? ''}&insurerOfferIds=${offerIds.join(',')}`

  return <Modal isOpen={isOpen} onClose={() => { }} size='full'>
    <ModalOverlay />
    <ModalContent bg='transparent' boxShadow='none'>
      <ModalBody px={{ base: 2, md: 4 }} py={{ base: 2, md: 6 }} alignContent='center'>
        <Box ref={bodyRef} mx='auto' w='100%' maxW={737}>
          {isLoading && <Loading mt={14} large spinnerProps={{ size: 'lg', thickness: '3px' }} color='primary.300' fontSize='lg' />}
          {isSuccess && <SendCalculationForm {...data} onSubmit={send} documentUrl={tiskUrl} />}
          {isError && <Alert status="error" mt={14}>
            <AlertTitle>
              Nepodařilo se načíst data.
            </AlertTitle>
          </Alert>}
        </Box>
      </ModalBody>
    </ModalContent>
  </Modal >
}

const sendCalculationSchema = yup.object({
  email: yup.string().required('Je nutné vyplnit e-mailovou adresu příjemce.').matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Neplatná e-mailová adresa.'),
  header: yup.string(),
  footer: yup.string(),
})

type SendCalculationFormValues = Required<Pick<SendCalculationEmailRequest, 'email'>> & Omit<SendCalculationEmailRequest, 'paramsId' | 'insurerOfferIds' | 'email'>

const SendCalculationForm = ({ email, subject, logoUrl, header, offers, footer, signature, disclaimer, documentUrl, onSubmit }: {
  email?: string
  subject: string
  logoUrl: string
  header: string
  offers?: string
  footer: string
  signature: string
  disclaimer: string
  documentUrl: string
  onSubmit: (data: SendCalculationFormValues) => Promise<void>
}) => {
  const stackRef = useRef<HTMLDivElement>(null)
  const methods = useForm<SendCalculationFormValues>({
    defaultValues: {
      email: email ?? '',
      header: header ?? '',
      footer: footer ?? ''
    },
    resolver: yupResolver(sendCalculationSchema),
  })

  const { getValues, setValue, handleSubmit, formState: { isSubmitting, isValid }, reset } = methods

  useEffect(() => {
    reset({
      email: email ?? '',
      header: header ?? '',
      footer: footer ?? ''
    })
  }, [email, header, footer, reset])
  
  return (
    <FormProvider {...methods}>
      <Stack ref={stackRef} as="form" gap={{ base: 3, md: 6 }} onSubmit={handleSubmit(onSubmit)}>
        <Stack padding="16px 24px 24px 24px" bg='white' borderRadius='10px' boxShadow='0 0 16px 0 rgba(0, 0, 0, 0.1)' border="1px solid #e5e7e9;">
          <HStack w='full' gap={6} alignItems='end'>
            <InputFormControl name="email"
              label="E-mailová adresa příjemce"
              placeholder='jan.novak@email.cz'
              isRequired
              w='full'
              flexGrow={1} />
            <Button colorScheme="primary" type="submit" size='lg' isLoading={isSubmitting} px={8} isDisabled={!isValid}>
              <HStack gap={4}>
                <Icon as={FontAwesomeIcon} icon={faPaperPlane} />
                <span>Odeslat</span>
              </HStack>
            </Button>
          </HStack>
          <Text fontSize='xs' color='gray.500'>
            Na e-mailovou adresu příjemce bude odeslána zpráva s vybranými kalkulacemi.<br />
            V náhledu níže můžete zprávu před odesláním upravit a zkontrolovat.
          </Text>
        </Stack>

        <Stack gap={0} padding="16px 24px 24px 24px" bg='white' borderRadius='10px' boxShadow='0 0 16px 0 rgba(0, 0, 0, 0.1)' border="1px solid #e5e7e9;" pos='relative'>
          <HStack justifyContent='space-between' mt={2}>
            <Ellipsis tooltipProps={{ maxW: '2xl' }}>
              <Text fontSize='lg' fontWeight='semibold'>{subject}</Text>
            </Ellipsis>

            <Button variant='ghost' size='sm' onClick={() => reset()} color='gray'>
              <HStack>
                <Icon as={FontAwesomeIcon} icon={faRefresh} />
                <Text as='span' whiteSpace='nowrap'>Vrátit změny</Text>
              </HStack>
            </Button>
          </HStack>
          <Divider my={4} />

          <Box textAlign='center' pb={8} pt={2.5}>
            <Image src={logoUrl} alt="Logo" maxW={170} />
          </Box>

          <Box pb={offers ? 8 : 0} m={-1.5}>
            <TextEditor
              toolbar={false}
              value={getValues('header') ?? ''}
              onBlur={(newValue) => setValue('header', newValue)}
              onChange={(newValue) => setValue('header', newValue)}
            />
          </Box>

          {offers &&
            <root.div>
              <Box dangerouslySetInnerHTML={{ __html: offers }} />
            </root.div>
          }

          <Box m={-1.5} pt={2}>
            <TextEditor
              toolbar={false}
              minHeight="50px"
              value={getValues('footer') ?? ''}
              onBlur={(newValue) => setValue('footer', newValue)}
              onChange={(newValue) => setValue('footer', newValue)}
            />
          </Box>

          <root.div>
            <Box dangerouslySetInnerHTML={{ __html: signature }} />
          </root.div>


          <Box margin='0 -24px -24px -24px' bg='#f2f4f8' padding="16px 24px" borderRadius='0 0 10px 10px'>
            <root.div>
              <Box dangerouslySetInnerHTML={{ __html: disclaimer }} />
            </root.div>
          </Box>
        </Stack>

        <Stack p={5} bg='white' borderRadius='10px' boxShadow='0 0 16px 0 rgba(0, 0, 0, 0.1)' border="1px solid #e5e7e9;" pos='relative'>
          <DocumentsListItem name="Nabídka pojistění" documentType="pdf" url={documentUrl}></DocumentsListItem>
        </Stack>
      </Stack>
    </FormProvider>
  )
}